import { parse, isValid } from 'date-fns';

import type { OptionalFieldDefinition, OptionalFieldConfig, OptionalFieldKey } from '@/entities/layout';
import { formatDateForApi, DATE_FORMAT } from '@/shared/ui/date-range-picker';

import type { OptionalFieldValue } from '../model/types';

export function isOptionalFieldKey(key: string): key is OptionalFieldKey {
  return /^optionalField[1-6]$/.test(key);
}

export function getFieldLabel(definition: OptionalFieldDefinition, locale: string = 'en'): string {
  const label = definition.label;

  switch (locale) {
    case 'es':
      return label.es || label.en;
    case 'fr':
      return label.fr || label.en;
    case 'po':
      return label.po || label.en;
    default:
      return label.en;
  }
}

export function validateOptionalField(
  value: OptionalFieldValue,
  definition: OptionalFieldDefinition | null | undefined,
  fieldKey: string
): string | undefined {
  if (!definition || !value || value.trim() === '') {
    return undefined;
  }

  const trimmedValue = value.trim();

  switch (definition.type) {
    case 'DATE': {
      const date = parse(trimmedValue, DATE_FORMAT, new Date());
      if (!isValid(date)) {
        return `${fieldKey} must be a valid date (yyyy-MM-dd)`;
      }
      break;
    }

    case 'NUMERIC': {
      const numericRegex = /^\d+$/;
      if (!numericRegex.test(trimmedValue)) {
        return `${fieldKey} must be a non-negative integer`;
      }

      const num = parseInt(trimmedValue, 10);
      if (num < 0) {
        return `${fieldKey} must be a non-negative integer`;
      }
      break;
    }

    case 'TEXT':
      break;
  }

  return undefined;
}

export function validateAllOptionalFields(
  values: Record<string, OptionalFieldValue>,
  configArray: OptionalFieldConfig[] | undefined
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!configArray || !Array.isArray(configArray)) {
    return errors;
  }

  configArray.forEach((fieldConfig) => {
    const [[fieldKey, definition]] = Object.entries(fieldConfig);

    if (definition && isOptionalFieldKey(fieldKey)) {
      const value = values[fieldKey];
      const error = validateOptionalField(value, definition, fieldKey);
      if (error) {
        errors[fieldKey] = error;
      }
    }
  });

  return errors;
}

export function formatDateValue(value: OptionalFieldValue): Date | null {
  if (!value) return null;

  const date = parse(value, DATE_FORMAT, new Date());
  return isValid(date) ? date : null;
}

export function formatDateForField(date?: Date): string | null {
  if (!date) return null;
  return formatDateForApi(date);
}
